/**
 * Theme Provider
 *
 * React context provider for theme management
 * Syncs with Zustand user preferences store for persistence
 */

import React, { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { COLOR_PRESETS, type ColorPreset, DEFAULT_PRESET, getPresetById } from '../tokens/colors';
import { DEFAULT_FONT_FAMILY, FONT_FAMILIES, type FontFamily, getFontFamilyById } from '../tokens/typography';
import type { DensityLevel } from '../tokens/spacing';
import { applyTheme, type ThemeConfig } from './theme-generator';
import { loadFont } from './font-loader';
import { useUserPreferencesStore } from '@/state/stores/user-preferences.store';

// ============================================================================
// TYPES
// ============================================================================

export interface Theme {
  /** Current color preset */
  colorPreset: ColorPreset;
  /** Light or dark mode */
  mode: 'light' | 'dark' | 'system';
  /** Selected font family */
  fontFamily: FontFamily;
  /** Density level */
  density: DensityLevel;
}

export interface ThemeContextValue extends Theme {
  /** Set color preset by ID */
  setColorPreset: (presetId: string) => void;
  /** Set theme mode */
  setMode: (mode: 'light' | 'dark' | 'system') => void;
  /** Set font family by ID */
  setFontFamily: (fontId: string) => void;
  /** Set density level */
  setDensity: (density: DensityLevel) => void;
  /** Get all available presets */
  availablePresets: ColorPreset[];
  /** Is dark mode active (resolved from system if mode === 'system') */
  isDarkMode: boolean;
}

// ============================================================================
// CONTEXT
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER PROPS
// ============================================================================

export interface ThemeProviderProps {
  children: ReactNode;
  /** Initial theme (optional) */
  initialTheme?: Partial<Theme>;
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, initialTheme }) => {
  // Get persisted preferences from Zustand store
  const {
    theme: storedTheme,
    setColorPreset: storeSetColorPreset,
    setThemeMode: storeSetThemeMode,
    setFontFamily: storeSetFontFamily,
  } = useUserPreferencesStore();

  // Initialize state from Zustand store or initial theme props
  const [colorPreset, setColorPresetState] = useState<ColorPreset>(() => {
    const storedPreset = getPresetById(storedTheme.colorPresetId);
    return storedPreset || initialTheme?.colorPreset || DEFAULT_PRESET;
  });
  const [mode, setModeState] = useState<'light' | 'dark' | 'system'>(
    storedTheme.mode || initialTheme?.mode || 'system'
  );
  const [fontFamily, setFontFamilyState] = useState<FontFamily>(
    (getFontFamilyById(storedTheme.fontFamilyId) || initialTheme?.fontFamily || DEFAULT_FONT_FAMILY) as FontFamily
  );
  const [density, setDensityState] = useState<DensityLevel>(
    initialTheme?.density || 'comfortable'
  );

  // Detect system preference
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false
  );

  // Resolved dark mode
  const isDarkMode = mode === 'dark' || (mode === 'system' && systemPrefersDark);

  // Sync with Zustand store when it changes externally
  useEffect(() => {
    const storedPreset = getPresetById(storedTheme.colorPresetId);
    if (storedPreset && storedPreset.id !== colorPreset.id) {
      setColorPresetState(storedPreset);
    }
  }, [storedTheme.colorPresetId, colorPreset.id]);

  useEffect(() => {
    if (storedTheme.mode !== mode) {
      setModeState(storedTheme.mode);
    }
  }, [storedTheme.mode, mode]);

  useEffect(() => {
    const storedFont = getFontFamilyById(storedTheme.fontFamilyId);
    if (storedFont && storedFont.id !== fontFamily.id) {
      setFontFamilyState(storedFont);
      // Load the font from Google Fonts
      loadFont(storedFont.id).catch(console.error);
    }
  }, [storedTheme.fontFamilyId, fontFamily.id]);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Apply theme whenever it changes
  useEffect(() => {
    const config: ThemeConfig = {
      colorPreset,
      mode: isDarkMode ? 'dark' : 'light',
      fontFamily: `${fontFamily.family}, ${fontFamily.fallback}`,
      density,
    };

    applyTheme(config);
  }, [colorPreset, isDarkMode, fontFamily, density]);

  // Load initial font
  useEffect(() => {
    loadFont(fontFamily.id).catch(console.error);
  }, [fontFamily.id]);

  // Set color preset - syncs to both local state and Zustand store
  const setColorPreset = useCallback((presetId: string) => {
    const preset = COLOR_PRESETS.find((p) => p.id === presetId);
    if (preset) {
      setColorPresetState(preset);
      storeSetColorPreset(presetId);
    }
  }, [storeSetColorPreset]);

  // Set mode - syncs to both local state and Zustand store
  const setMode = useCallback((newMode: 'light' | 'dark' | 'system') => {
    setModeState(newMode);
    storeSetThemeMode(newMode);
  }, [storeSetThemeMode]);

  // Set font family - syncs to both local state and Zustand store
  const setFontFamily = useCallback((fontId: string) => {
    const font = FONT_FAMILIES.find((f: FontFamily) => f.id === fontId);
    if (font) {
      setFontFamilyState(font);
      storeSetFontFamily(fontId);
      // Load the font from Google Fonts
      loadFont(fontId).catch(console.error);
    }
  }, [storeSetFontFamily]);

  // Set density
  const setDensity = useCallback((newDensity: DensityLevel) => {
    setDensityState(newDensity);
  }, []);

  const value: ThemeContextValue = {
    colorPreset,
    mode,
    fontFamily,
    density,
    setColorPreset,
    setMode,
    setFontFamily,
    setDensity,
    availablePresets: COLOR_PRESETS,
    isDarkMode,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * Use theme context
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
